import React, { useState, useEffect, useMemo, useRef } from "react";
import "./ProductDropDown.css";

/**
 * Feature flags live in localStorage under "kleido-feature-list",
 * e.g. ["KE", "KW", "KR", "KG", "KC", "KP", "KA"].
 * Only these three map to products in this dropdown.
 */
const FEATURE_KEYS = [
  "kleido-feature-list",        // ← current key
  "Kleido-selected-features",   // legacy spellings, safe to drop later
  "kleido-selected-features",
  "features",
];

const FEATURE = {
  GATHER: "KE",
  SKY: "KW",
  INSIGHT: "KR",
};

/** Old flag names, kept so existing sessions don't lose their products. */
const LEGACY_ALIASES = {
  OOTSHAV: FEATURE.GATHER,
  KLAMITY: FEATURE.SKY,
  DELUZON: FEATURE.INSIGHT,
};

/** Accepts a JSON array or a comma-separated string; returns normalised codes. */
function parseFeatureFlags(raw) {
  if (!raw) return [];
  let list = [];
  try {
    const parsed = JSON.parse(raw);
    list = Array.isArray(parsed) ? parsed : String(parsed).split(",");
  } catch {
    list = String(raw).split(",");
  }
  return list
    .map((f) => String(f).trim().toUpperCase())
    .filter(Boolean)
    .map((f) => LEGACY_ALIASES[f] || f);
}

/** Reads the first key that actually holds something. */
function readFeatureFlags() {
  for (const key of FEATURE_KEYS) {
    const raw = localStorage.getItem(key);
    if (raw) {
      const parsed = parseFeatureFlags(raw);
      if (parsed.length) return parsed;
    }
  }
  return [];
}

const ACTIVE_COLOR = "#4caf50";
const INACTIVE_COLOR = "#ef4444";
const TILE_FILL = "#ededed";

/** Circular badge showing the two-letter feature code. */
const CodeBadge = ({ code, enabled }) => (
  <span
    aria-hidden="true"
    style={{
      width: 32,
      height: 32,
      borderRadius: "50%",
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: enabled ? "#ffffff" : "rgba(255, 255, 255, 0.35)",
      color: "#000000",
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.06em",
      lineHeight: 1,
      border: "1px solid rgba(0, 0, 0, 0.12)",
    }}
  >
    {code}
  </span>
);

/**
 * Four rounded squares in a 2×2 block.
 * Squares 1–3 track Gather / Sky / Insight;
 * square 4 is the aggregate, so "everything on" reads as all-green.
 */
export const ProductGridIcon = ({ size = 20, statuses = [false, false, false, false] }) => {
  const tiles = [
    { x: 2.5, y: 2.5 },
    { x: 13, y: 2.5 },
    { x: 2.5, y: 13 },
    { x: 13, y: 13 },
  ];
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
      {tiles.map((t, i) => (
        <rect
          key={i}
          x={t.x}
          y={t.y}
          width="8.5"
          height="8.5"
          rx="2.6"
          fill={TILE_FILL}
          stroke={statuses[i] ? ACTIVE_COLOR : INACTIVE_COLOR}
          strokeWidth="1.6"
        />
      ))}
    </svg>
  );
};

const ProductList = () => {
  const [open, setOpen] = useState(false);
  const [userid, setUserid] = useState(null);
  const [flags, setFlags] = useState([]);
  const wrapperRef = useRef(null);

  // ✅ Logged-in user
  useEffect(() => {
    try {
      const userLoggedInString = localStorage.getItem("userLoggedin");
      if (userLoggedInString) {
        const userLoggedIn = JSON.parse(userLoggedInString);
        setUserid(userLoggedIn.userId || userLoggedIn.userid || userLoggedIn.id);
      }
    } catch (error) {
      console.error("❌ Error reading userLoggedin from localStorage:", error);
    }
  }, []);

  // ✅ Selected feature flags
  useEffect(() => {
    try {
      const parsed = readFeatureFlags();
      setFlags(parsed);
      console.log("✅ Selected features:", parsed);
    } catch (error) {
      console.error("❌ Error reading feature flags from localStorage:", error);
      setFlags([]);
    }
  }, []);

  // ✅ Close on outside click / Escape
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // ✅ All three products, each gated by its own flag
  const products = useMemo(() => {
    if (!userid) return [];
    return [
      {
        name: "Kleido Gather",
        caption: "Discover and manage events",
        link: `http://events.kleido.ai/home/${userid}`,
        featureCode: FEATURE.GATHER,
        enabled: flags.includes(FEATURE.GATHER),
      },
      {
        name: "Kleido Sky",
        caption: "Forecasts and climate insight",
        link: `http://weather.kleido.ai/home/${userid}`,
        featureCode: FEATURE.SKY,
        enabled: flags.includes(FEATURE.SKY),
      },
      {
        name: "Kleido Insight",
        caption: "Ratings and verified reviews",
        link: `http://review.kleido.ai/home/${userid}`,
        featureCode: FEATURE.INSIGHT,
        enabled: flags.includes(FEATURE.INSIGHT),
      },
    ];
  }, [userid, flags]);

  const enabledCount = products.filter((p) => p.enabled).length;
  const allEnabled = products.length > 0 && enabledCount === products.length;

  // squares 1–3 = the products in order, square 4 = "all active"
  const iconStatuses = [
    products[0]?.enabled ?? false,
    products[1]?.enabled ?? false,
    products[2]?.enabled ?? false,
    allEnabled,
  ];

  const handleProductClick = (product) => {
    if (!product.enabled) return;
    window.location.href = product.link;
    setOpen(false);
  };

  if (!userid) return null;

  return (
    <div className="nb-product-list" ref={wrapperRef} style={{ position: "relative" }}>

      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Products"
        title={
          allEnabled
            ? "All products active"
            : `${enabledCount} of ${products.length} products active`
        }
        className="rounded-lg p-2 hover:bg-secondary transition-colors"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "transparent",
          border: "none",
          cursor: "pointer",
          opacity: open ? 1 : 0.9,
          transition: "opacity 0.2s ease",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
        onMouseLeave={(e) => (e.currentTarget.style.opacity = open ? "1" : "0.9")}
      >
        <ProductGridIcon size={20} statuses={iconStatuses} />
      </button>

      {/* Popup — translucent glass over the black background */}
      {open && (
        <div
          className="nb-product-list-container"
          role="menu"
          style={{
            position: "absolute",
            top: "100%",
            right: 0,
            marginTop: "10px",
            minWidth: "240px",
            padding: "8px",
            borderRadius: "14px",
            zIndex: 1000,
            background: "black",
            backdropFilter: "blur(18px) saturate(140%)",
            WebkitBackdropFilter: "blur(18px) saturate(140%)",
            border: "1px solid rgba(255, 255, 255, 0.12)",
            boxShadow: "0 18px 45px rgba(0, 0, 0, 0.55)",
          }}
        >
          {products.map((product) => (
            <div
              className="nb-product-menu-container"
              key={product.featureCode}
              role="menuitem"
              aria-disabled={!product.enabled}
              onClick={() => handleProductClick(product)}
              title={product.enabled ? product.link : `${product.name} is not activated`}
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 12,
                padding: "10px 12px",
                borderRadius: "10px",
                cursor: product.enabled ? "pointer" : "default",
                opacity: product.enabled ? 1 : 0.38,
                transition: "background-color 0.2s ease",
              }}
              onMouseEnter={(e) => {
                if (product.enabled) {
                  e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.09)";
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              <CodeBadge code={product.featureCode} enabled={product.enabled} />

              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ color: "white", fontSize: 14, fontWeight: 600, lineHeight: 1.2 }}>
                  {product.name}
                </div>
                <div
                  style={{
                    color: "rgba(255, 255, 255, 0.55)",
                    fontSize: 11,
                    marginTop: 2,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {product.enabled ? product.caption : "Not activated"}
                </div>
              </div>

              <span
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  flexShrink: 0,
                  background: product.enabled ? ACTIVE_COLOR : INACTIVE_COLOR,
                }}
              />
            </div>
          ))}

          {enabledCount === 0 && (
            <div
              style={{
                margin: "6px 4px 2px",
                paddingTop: 8,
                borderTop: "1px solid rgba(255, 255, 255, 0.1)",
                color: "rgba(255, 255, 255, 0.5)",
                fontSize: 11,
                lineHeight: 1.5,
              }}
            >
              No products activated yet. Activate them from Explore.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductList;